import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CampaignListItem, CampaignListSortRule } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import {
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableHeaderStickyTopClass,
  contactsTableScrollViewportClass,
  contactsTableShellClass,
} from "../contacts/contacts-table-presentation.js";
import {
  CAMPAIGN_LIST_ACTIONS_WIDTH,
  CAMPAIGN_LIST_CHECKBOX_WIDTH,
  campaignColumnSortField,
  campaignFixedColumnsWidth,
  campaignVisibleColumnsWidth,
  shouldSuppressCampaignRowNavigation,
  type CampaignListDataColumnId,
} from "./campaign-list-table.js";
import {
  CAMPAIGN_LIST_COLUMN_META,
  currentPageSelectionState,
  formatCampaignDate,
  syncCheckboxIndeterminate,
  toggleCurrentPageSelection,
} from "./campaigns-presentation.js";
import { CampaignRowActionsMenu } from "./CampaignRowActionsMenu.js";
import { CampaignStatusBadge } from "./CampaignStatusBadge.js";

/** `border-separate` is required for sticky headers, so row separators move onto the cells. */
const bodyCellClass = cn(contactsTableBodyCellClass, "border-b border-b-line/45");

function sortIndicator(
  sort: readonly CampaignListSortRule[],
  columnId: CampaignListDataColumnId,
): string | null {
  const field = campaignColumnSortField(columnId);
  if (field == null) return null;
  const match = sort.find((rule) => rule.field === field);
  if (!match) return null;
  return match.direction === "asc" ? "↑" : "↓";
}

function CampaignColumnResizeHandle({
  label,
  onResizeStart,
}: {
  label: string;
  onResizeStart: (clientX: number) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid="campaign-column-resize"
      className="pointer-fine:absolute pointer-fine:end-0 pointer-fine:top-0 pointer-fine:z-10 pointer-fine:block pointer-fine:h-full pointer-fine:w-1.5 pointer-fine:cursor-col-resize pointer-fine:bg-transparent pointer-fine:hover:bg-accent/40 hidden"
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onResizeStart(event.clientX);
      }}
    />
  );
}

export function CampaignsTable({
  items,
  selectedIds,
  canDelete,
  busy,
  sort,
  visibleColumns,
  widths,
  onContainerWidth,
  onToggleSelect,
  onSelectPage,
  onReorderColumn,
  onResizeColumn,
  onCopyId,
  onDelete,
}: {
  items: readonly CampaignListItem[];
  selectedIds: ReadonlySet<number>;
  canDelete: boolean;
  busy: boolean;
  sort: readonly CampaignListSortRule[];
  visibleColumns: readonly CampaignListDataColumnId[];
  widths: Record<CampaignListDataColumnId, number>;
  onContainerWidth: (width: number) => void;
  onToggleSelect: (campaignId: number) => void;
  onSelectPage: (ids: readonly number[]) => void;
  onReorderColumn: (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => void;
  onResizeColumn: (id: CampaignListDataColumnId, width: number) => void;
  onCopyId: (id: string) => void;
  onDelete: (ids: readonly number[]) => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language.startsWith("he") ? "he-IL" : "en-US";
  const shellRef = useRef<HTMLDivElement | null>(null);
  const dragFromRef = useRef<CampaignListDataColumnId | null>(null);
  const resizeRef = useRef<{ id: CampaignListDataColumnId; startX: number; startWidth: number; rtl: boolean } | null>(
    null,
  );
  const pageSelection = currentPageSelectionState(
    items.map((item) => ({ id: Number(item.id) })),
    selectedIds,
  );
  const bindSelectPageCheckbox = (node: HTMLInputElement | null) => {
    syncCheckboxIndeterminate(node, pageSelection);
  };
  const minTableWidth =
    campaignFixedColumnsWidth(canDelete) + campaignVisibleColumnsWidth(visibleColumns, widths);

  useEffect(() => {
    const node = shellRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      onContainerWidth(width);
    });
    observer.observe(node);
    onContainerWidth(node.clientWidth);
    return () => observer.disconnect();
  }, [onContainerWidth]);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const active = resizeRef.current;
      if (active == null) return;
      const delta = active.rtl ? active.startX - event.clientX : event.clientX - active.startX;
      onResizeColumn(active.id, active.startWidth + delta);
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onResizeColumn]);

  const renderCell = (item: CampaignListItem, columnId: CampaignListDataColumnId) => {
    switch (columnId) {
      case "name":
        return (
          <Link
            to={`/campaigns/${item.id}`}
            className="block min-w-0 truncate font-semibold text-foreground no-underline hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              if (shouldSuppressCampaignRowNavigation()) event.preventDefault();
            }}
          >
            {item.name}
          </Link>
        );
      case "audience":
        return item.snapshotStale || item.readyRecipientCount == null
          ? t("campaigns.list.audiencePending")
          : t("campaigns.detail.recipientsCount", { count: item.readyRecipientCount });
      case "status":
        return <CampaignStatusBadge status={item.status} />;
      case "updated":
        return (
          <span className="whitespace-nowrap tabular-nums text-muted" dir="ltr">
            {formatCampaignDate(item.updatedAt, locale) ?? "—"}
          </span>
        );
      case "scheduled":
        return (
          <span className="whitespace-nowrap tabular-nums text-muted" dir="ltr">
            {item.scheduledAt ? formatCampaignDate(item.scheduledAt, locale) : "—"}
          </span>
        );
      default: {
        const _exhaustive: never = columnId;
        return _exhaustive;
      }
    }
  };

  return (
    <div
      ref={shellRef}
      className={cn(contactsTableShellClass, contactsTableScrollViewportClass)}
      data-testid="campaign-list-table"
    >
      <table
        className="w-full table-fixed border-separate border-spacing-0 text-[0.8125rem]"
        style={{ minWidth: `${minTableWidth}px`, width: "100%" }}
      >
        <colgroup>
          {canDelete ? <col style={{ width: CAMPAIGN_LIST_CHECKBOX_WIDTH }} /> : null}
          {visibleColumns.map((columnId) => (
            <col key={columnId} style={{ width: widths[columnId] }} />
          ))}
          <col style={{ width: CAMPAIGN_LIST_ACTIONS_WIDTH }} />
        </colgroup>
        <thead>
          <tr className={contactsTableHeadRowClass}>
            {canDelete ? (
              <th
                className={cn(
                  contactsTableHeaderCellClass,
                  contactsTableHeaderStickyTopClass,
                  "w-10 p-0 text-start",
                )}
              >
                <label className="inline-grid size-9 cursor-pointer place-items-center">
                  <input
                    ref={bindSelectPageCheckbox}
                    type="checkbox"
                    aria-label={t("campaigns.list.selectPage")}
                    checked={pageSelection === "all"}
                    onChange={() => {
                      onSelectPage([
                        ...toggleCurrentPageSelection(
                          items.map((item) => ({ id: Number(item.id) })),
                          selectedIds,
                        ),
                      ]);
                    }}
                    className="size-4 accent-[var(--color-accent)]"
                    data-testid="campaign-list-select-page"
                  />
                </label>
              </th>
            ) : null}
            {visibleColumns.map((columnId) => {
              const Icon = CAMPAIGN_LIST_COLUMN_META[columnId].icon;
              const indicator = sortIndicator(sort, columnId);
              return (
                <th
                  key={columnId}
                  // `sticky` is itself a positioned value, so the resize handle
                  // still anchors to this cell without an explicit `relative`.
                  className={cn(
                    contactsTableHeaderCellClass,
                    contactsTableHeaderStickyTopClass,
                    "text-start",
                  )}
                  draggable
                  onDragStart={() => {
                    dragFromRef.current = columnId;
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    const from = dragFromRef.current;
                    dragFromRef.current = null;
                    if (from) onReorderColumn(from, columnId);
                  }}
                  data-testid={`campaign-column-${columnId}`}
                >
                  <span className={contactsTableHeaderLabelClass}>
                    <span className={contactsTableHeaderIconWrapClass} aria-hidden="true">
                      <Icon size={12} />
                    </span>
                    <span>{t(`campaigns.list.columns.${columnId}`)}</span>
                    {indicator ? (
                      <span className="text-[0.65rem] font-semibold text-accent" aria-hidden="true">
                        {indicator}
                      </span>
                    ) : null}
                  </span>
                  <CampaignColumnResizeHandle
                    label={t("campaigns.list.resizeColumn", {
                      column: t(`campaigns.list.columns.${columnId}`),
                    })}
                    onResizeStart={(clientX) => {
                      const rtl =
                        typeof document !== "undefined" &&
                        getComputedStyle(document.documentElement).direction === "rtl";
                      resizeRef.current = {
                        id: columnId,
                        startX: clientX,
                        startWidth: widths[columnId],
                        rtl,
                      };
                    }}
                  />
                </th>
              );
            })}
            <th
              className={cn(
                contactsTableHeaderCellClass,
                contactsTableHeaderStickyTopClass,
                "p-0 text-end",
              )}
            >
              <span className="sr-only">{t("campaigns.list.rowActions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const selected = selectedIds.has(Number(item.id));
            return (
              <tr
                key={item.id}
                className={cn(contactsTableBodyRowClass, "cursor-pointer")}
                data-selected={selected || undefined}
                data-testid="campaign-list-row"
                onClick={() => {
                  if (shouldSuppressCampaignRowNavigation()) return;
                  navigate(`/campaigns/${item.id}`);
                }}
              >
                {canDelete ? (
                  <td
                    className={cn(bodyCellClass, "w-10 p-0")}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <label className="inline-grid size-9 cursor-pointer place-items-center">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSelect(Number(item.id))}
                        aria-label={item.name}
                        className="size-4 accent-[var(--color-accent)]"
                      />
                    </label>
                  </td>
                ) : null}
                {visibleColumns.map((columnId) => (
                  <td
                    key={columnId}
                    className={cn(bodyCellClass, "text-start", columnId === "name" && "min-w-0")}
                    data-testid={`campaign-cell-${columnId}`}
                  >
                    {renderCell(item, columnId)}
                  </td>
                ))}
                <td
                  className={cn(bodyCellClass, "p-0 text-end")}
                  data-stop-row-nav=""
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <CampaignRowActionsMenu
                    item={item}
                    canDelete={canDelete}
                    busy={busy}
                    onOpen={() => navigate(`/campaigns/${item.id}`)}
                    onCopyId={() => onCopyId(String(item.id))}
                    onDelete={() => onDelete([Number(item.id)])}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
